import * as yup from 'yup';

const schema = {
    index: yup.object().shape({
        selected: yup.string()
            .oneOf(['recommendationTool', 'searchByFundName', 'cellReference'], 'Invalid selection')
            .required('Traded fund lookup is required'),
        index: yup.string()
            .when('selected', {
                is: (val: string) => val !== 'cellReference',
                then: (s: any) => s.required('Traded fund is required'),
                otherwise: (s: any) => s.notRequired()
            }),
        exchange: yup.string()
            .when('selected', {
                is: 'cellReference',
                then: (s: any) => s.required('Exchange and Ticker are required'),
                otherwise: (s: any) => s.notRequired()
            }),
        ticker: yup.string()
            .when('selected', {
                is: 'cellReference',
                then: (s: any) => s.required('Exchange and Ticker are required'),
                otherwise: (s: any) => s.notRequired()
            }),
    }),
    companyIdentification: yup.object().shape({
        selected: yup.string()
            .oneOf(['Search', 'Cell Reference'], 'Selected value must be either "Search" or "Cell Reference"')
            .required('Company identification is required'),
        searchInput: yup.string()
            .when('selected', {
                is: 'Search',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Search input is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
        exchange: yup.string()
            .when('selected', {
                is: 'Cell Reference',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Exchange and Ticker are required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
        ticker: yup.string()
            .when('selected', {
                is: 'Cell Reference',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Exchange and Ticker are required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
    }),
    timePeriod: yup.object().shape({
        selected: yup.string().required('Time period is required'),
        beginDate: yup.string()
            .when('selected', {
                is: 'Date',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Beginning date is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
        endDate: yup.string()
            .when('selected', {
                is: 'Date',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Ending date is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
        beginDateCellReference: yup.string()
            .when('selected', {
                is: 'Cell Reference',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Beginning date cell reference is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
        endDateCellReference: yup.string()
            .when('selected', {
                is: 'Cell Reference',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Ending date cell reference is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
    }),
    asOfDate: yup.object().shape({
        selected: yup.string().required('As of date is required'),
        date: yup.string()
            .when('selected', {
                is: 'Date',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Date is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
        dateCellReference: yup.string()
            .when('selected', {
                is: 'Cell Reference',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Date cell reference is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
    }),
    currencyType: yup.object().shape({
        selected: yup.string().required('Currency is required'),
        currency: yup.string()
            .when('selected', {
                is: 'Currency',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Currency is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
        currencyCellReference: yup.string()
            .when('selected', {
                is: 'Cell Reference',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Currency cell reference is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
    }),
    formulaLocation: yup.string().required('Formula location is required'),

    // Currency Data
    dataSelection: yup.string().required('Data selection is required'),
    baseCurrency: yup.object().shape({
        selected: yup.string()
            .oneOf(['Value', 'Cell Reference'], 'Selected value must be either "Value" or "Cell Reference"')
            .required('Base currency is required'),
        currency: yup.string()
            .when('selected', {
                is: 'Value',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Currency is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
        currencyCellReference: yup.string()
            .when('selected', {
                is: 'Cell Reference',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Currency cell reference is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
    }),
    quoteCurrency: yup.object().shape({
        selected: yup.string()
            .oneOf(['Value', 'Cell Reference'], 'Selected value must be either "Value" or "Cell Reference"')
            .required('Quote currency is required'),
        currency: yup.string()
            .when('selected', {
                is: 'Value',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Currency is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
        currencyCellReference: yup.string()
            .when('selected', {
                is: 'Cell Reference',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Currency cell reference is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
    }),

    date: yup.object().shape({
        selected: yup.string()
            .oneOf(['Date', 'Cell Reference'], 'Selected value must be either "Date" or "Cell Reference"')
            .required('Date is required'),
        date: yup.string()
            .when('selected', {
                is: 'Date',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Date is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
        dateCellReference: yup.string()
            .when('selected', {
                is: 'Cell Reference',
                then: (schema: { required: (arg0: string) => any; }) => schema.required('Date cell reference is required'),
                otherwise: (schema: { notRequired: () => any; }) => schema.notRequired()
            }),
    }),

    // Capitalization Actuals
    dataSource: yup.string().required('Data source is required'),
    dataType: yup.string().required('Data type is required'),
    dataScale: yup.string()
        .oneOf(['Actual', 'Thousands', 'Millions', 'Billions'], 'Data scale must be one of: Actual, Thousands, Millions, Billions')
        .required('Data scale is required'),
    asOfDateActuals: yup.mixed()
        .test('is-valid-date', 'As of date is required', (value) => {
            return value !== null && value !== undefined;
        })
        .required('As of date is required'),
}

export default schema
